import {
  CONTRACTS_VERSION,
  QueryRequestSchema,
  type QueryRequest,
  type QueryRequestInput,
} from '@aibi/contracts';
import { err, ok, type Result } from '@aibi/shared';
import { planAnalysis } from './build-query-plan.js';
import {
  AnalysisPlanError,
  type AnalysisDiagnostic,
  type AnalysisDiagnosticCode,
} from './diagnostics.js';
import type { QueryPlan } from './query-plan.js';

export interface BuildQueryRequestOptions {
  /** Id de la solicitud; por defecto `req.<huella del plan>` (determinista). */
  readonly id?: string;
  readonly fields?: QueryRequestInput['fields'];
  readonly execution?: QueryRequestInput['execution'];
  readonly pagination?: QueryRequestInput['pagination'];
  readonly cache?: QueryRequestInput['cache'];
  /** Metadatos de trazabilidad; `requestedAt` lo aporta el llamador (el motor no lee el reloj). */
  readonly metadata?: QueryRequestInput['metadata'];
}

export interface BuiltQueryRequest {
  readonly request: QueryRequest;
  /** Plan validado de la solicitud; el Query Engine lo compila y ejecuta. */
  readonly plan: QueryPlan;
}

/**
 * Construye un `QueryRequest` valido con el contrato existente a partir de un analisis.
 * Primero planifica: si el plan no es valido no se genera la solicitud. La solicitud
 * lleva el Analysis State (no el plan) y no ejecuta nada.
 *
 * @example
 * const built = buildQueryRequest(state, model, { pagination: { pageSize: 100 } });
 * if (built.ok) executor.execute(built.value.request, built.value.plan);
 */
export function buildQueryRequest(
  state: unknown,
  model: unknown,
  options: BuildQueryRequestOptions = {},
): Result<BuiltQueryRequest, AnalysisPlanError> {
  const { shape } = QueryRequestSchema;
  const execution = shape.execution.safeParse(options.execution);
  const pagination = shape.pagination.safeParse(options.pagination);
  const optionIssues: AnalysisDiagnostic[] = [
    ...(execution.success
      ? []
      : issueDiagnostics(execution.error.issues, 'execution', 'invalid-state')),
    ...(pagination.success
      ? []
      : issueDiagnostics(pagination.error.issues, 'pagination', 'invalid-pagination')),
  ];
  if (!execution.success || !pagination.success) {
    return err(new AnalysisPlanError('Opciones de consulta no validas.', optionIssues));
  }

  const planned = planAnalysis(state, model, {
    pagination: {
      offset: pagination.data.offset,
      pageSize: pagination.data.pageSize,
      maxRows: execution.data.maxRows,
    },
  });
  if (!planned.ok) return planned;
  const { plan } = planned.value;

  const parsed = QueryRequestSchema.safeParse({
    schemaVersion: CONTRACTS_VERSION,
    id: options.id ?? `req.${plan.fingerprint}`,
    semanticModelId: plan.semanticModelId,
    analysisState: planned.value.state,
    ...(options.fields ? { fields: options.fields } : {}),
    execution: execution.data,
    pagination: pagination.data,
    ...(options.cache ? { cache: options.cache } : {}),
    ...(options.metadata ? { metadata: options.metadata } : {}),
  });
  if (!parsed.success) {
    return err(
      new AnalysisPlanError(
        'No se puede construir la solicitud de consulta.',
        issueDiagnostics(parsed.error.issues, '', 'invalid-state'),
      ),
    );
  }
  return ok({ request: parsed.data, plan });
}

interface ContractIssue {
  readonly path: readonly PropertyKey[];
  readonly message: string;
}

function issueDiagnostics(
  issues: readonly ContractIssue[],
  prefix: string,
  code: AnalysisDiagnosticCode,
): AnalysisDiagnostic[] {
  return issues.map((issue) => {
    const path = [prefix, ...issue.path.map(String)].filter((part) => part.length > 0).join('.');
    return {
      severity: 'error',
      code: path.startsWith('fields.') ? 'missing-reference' : code,
      path,
      message: issue.message,
    };
  });
}
